import React, { useEffect } from 'react';
import { SEOMetadata, DEFAULT_SEO, getJsonLdSchema } from '../config/seoConfig';

interface SEOHeadProps {
  metadata?: Partial<SEOMetadata>;
}

/**
 * Composant SEOHead pour gérer les métadonnées dynamiques
 * Inclut: title, description, Open Graph, Twitter Card, JSON-LD
 */
export const SEOHead: React.FC<SEOHeadProps> = ({ metadata = {} }) => {
  const seo = { ...DEFAULT_SEO, ...metadata } as SEOMetadata;

  useEffect(() => {
    // Mettre à jour le titre
    document.title = seo.title;

    // Mettre à jour ou créer les métadonnées
    const updateMeta = (name: string, content: string, isProperty = false) => {
      let element = document.querySelector(
        isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`
      );
      if (!element) {
        element = document.createElement('meta');
        if (isProperty) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Métadonnées basiques
    updateMeta('description', seo.description);
    updateMeta('robots', 'index, follow');
    updateMeta('language', 'French');
    updateMeta('revisit-after', '7 days');

    // Open Graph (Facebook, LinkedIn, etc.)
    updateMeta('og:title', seo.title, true);
    updateMeta('og:description', seo.description, true);
    updateMeta('og:image', (seo.image || DEFAULT_SEO.image) as string, true);
    updateMeta('og:url', (seo.url || DEFAULT_SEO.url) as string, true);
    updateMeta('og:type', (seo.type || 'website') as string, true);
    updateMeta('og:locale', 'fr_FR', true);

    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', seo.title);
    updateMeta('twitter:description', seo.description);
    updateMeta('twitter:image', (seo.image || DEFAULT_SEO.image) as string);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', (seo.url || DEFAULT_SEO.url) as string);

    // JSON-LD Schema
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify(getJsonLdSchema(seo));

    // Log pour vérifier
    console.log('🔍 SEO Updated:', {
      title: seo.title,
      description: seo.description,
      url: seo.url,
    });
  }, [seo]);

  // Ce composant ne rend rien, il gère uniquement les métadonnées
  return null;
};
